export const MARKET_QC = "QC";
export const MARKET_ON = "ON";

export function normalizeLocale(locale) {
  return locale && locale.indexOf("fr") === 0 ? "fr" : "en";
}

export function getCurrentMarket(country) {
  return country || localStorage.getItem("countryCode") || MARKET_QC;
}

// Portaus company ids. Providers (and eventually products) are partitioned by
// company so boires.ca never surfaces Négoce/PlanVin sheets.
//
// Two different things, scoped two different ways (per Alex, backend,
// 2026-07-21) — do not conflate them:
//
//   fiche produit / product sheet  -> scoped by `portausCompanyId`
//   produit en inventaire          -> scoped by INVENTORY CODE, not company
//
//   /providers/elastic                       honours portausCompanyId
//   /v1/products/search/                     honours portausCompanyId
//   /v1/product-inventories/search/          honours portausCompanyId (since
//                                            2026-09, on top of inventory code)
//   /countries/?withResultsIn=providers      ignores it  (origine filter)
//   /attributes/?withResultsIn=PROVIDER      ignores it  (attribute filter)
//
// The filter endpoints returning identical results for every company id is a
// real gap: the param is silently dropped rather than rejected, so the panel
// offers origins/attributes that cannot match any provider.
//
// Inventory codes are NOT unique across companies: "IP" exists for both
// Boires (inventory 1, company 1) and PlanVin (inventory 17, company 4), so
// filtering the QC listing by code alone let the Château Picoron rows through.
// Alex added portausCompanyId to /product-inventories/search/ for this.
export const PORTAUS_COMPANY = {
  BOIRES: 1,
  BOIRES_CANADA: 2,
  NEGOCE: 3,
  PLANVIN: 4,
};

export const PORTAUS_COMPANY_LABELS = {
  1: "Boires",
  2: "Boires Canada",
  3: "Négoce",
  4: "PlanVin",
};

export const PORTAUS_COMPANY_OVERRIDE_KEY = "portausCompanyOverride";

// The real market → company mapping. Deliberately ignores the debug override:
// checkout and registration must always write the customer's true company, or
// we create orders against the wrong entity.
export function getPortausCompanyId(market) {
  return market === MARKET_ON
    ? PORTAUS_COMPANY.BOIRES_CANADA
    : PORTAUS_COMPANY.BOIRES;
}

export function getPortausCompanyOverride() {
  const raw = localStorage.getItem(PORTAUS_COMPANY_OVERRIDE_KEY);
  const id = parseInt(raw, 10);

  return PORTAUS_COMPANY_LABELS[id] ? id : null;
}

// Override-aware variant — CONTENT BROWSING ONLY (provider listings/search).
// Lets us preview the future Négoce/PlanVin sites via `?company=3` before those
// sites exist. Never use this for anything that writes data.
export function getBrowsingPortausCompanyId(market) {
  return getPortausCompanyOverride() || getPortausCompanyId(market);
}

export function getConsignmentInventory(market) {
  return market === MARKET_ON ? "PI" : "IP";
}

export function getBoardInventory(market) {
  return market === MARKET_ON ? "LCBO" : "SAQ";
}

export function getDefaultAvailability(market) {
  if (market === MARKET_ON) {
    return ["LCBO", "PI", "MERCH_TORONTO"];
  }

  return ["IP", "SAQ", "Brouhh", "MERCH_MTL", "BERGEVILLE"];
}

// The `inventory` query param serves two very different purposes:
//  - on the boutique pages it names a single retail store (STORE_MTL,
//    STORE_QC, STORE_TORONTO) and puts the page in "shop" mode;
//  - on the agency products page it carries the *warehouse* selection
//    (IP, SAQ, Brouhh, ...) so the entrepôt filter survives pagination,
//    language/region/tax switches and shared URLs.
// Only the first meaning may switch the page into shop mode, so every
// "is this the boutique?" test must go through this helper instead of
// checking that `inventory` is merely present.
export function isBoutiqueInventory(inventory) {
  const codes = Array.isArray(inventory) ? inventory : [inventory];
  return codes.some((code) => typeof code === "string" && code.indexOf("STORE_") === 0);
}

export function getSocialLinks(market) {
  return {
    instagram:
      market === MARKET_ON
        ? "https://www.instagram.com/boires.on/"
        : "https://www.instagram.com/boires.ca/",
    facebook:
      market === MARKET_ON
        ? "https://www.facebook.com/boires.on/"
        : "https://www.facebook.com/boires.ca/",
  };
}

export function parseAvailabilityStatusQuery(show) {
  switch (show) {
    case "AVAILABLE":
      return ["AVAILABLE"];
    case "UPCOMING":
      return ["UPCOMING"];
    case "AVAILABLE_UPCOMING":
    default:
      return ["AVAILABLE", "UPCOMING"];
  }
}

export function availabilityStatusToQuery(statuses) {
  if (statuses == null || statuses.length === 0) {
    return null;
  }

  const hasAvailable = statuses.indexOf("AVAILABLE") > -1;
  const hasUpcoming = statuses.indexOf("UPCOMING") > -1;

  if (hasAvailable && hasUpcoming) {
    return "AVAILABLE_UPCOMING";
  }

  if (hasAvailable) {
    return "AVAILABLE";
  }

  if (hasUpcoming) {
    return "UPCOMING";
  }

  return statuses[0] || null;
}

export function getEmbeddedNewsletterCustomerType(market) {
  return market === MARKET_ON ? "INDIVIDUAL_ON" : "INDIVIDUAL_QC";
}

export function getNewsletterMarket(customerType, fallbackMarket) {
  if (customerType && customerType.indexOf("_ON") > -1) {
    return MARKET_ON;
  }

  if (customerType && customerType.indexOf("_QC") > -1) {
    return MARKET_QC;
  }

  return fallbackMarket || MARKET_QC;
}
